import { NavLink, Outlet, useLocation, Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  LayoutDashboard, Package, FolderTree, Tag, Boxes, Truck, ClipboardList,
  Warehouse, UsersRound, UserCircle2, Receipt as ReceiptIcon, Settings, BarChart3,
  LogOut, ChevronRight, Megaphone, LayoutTemplate, Power, MapPin,
  Menu, X, Store as StoreIcon, Palette, Upload as UploadIcon, MonitorSmartphone,
  Bell, Search, ShoppingBag, Sparkles, Users, Wallet, HelpCircle, ShieldCheck,
  FileClock, FileText, ShoppingCart,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth, canPOS } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, auth as authStore } from "@/lib/api";
import { toast } from "sonner";

// Sprint Phase 2 — a store's owning admin may hold either the canonical
// `STORE_ADMIN` role or the legacy `STORE_OWNER` alias. Every rolesAllowed
// array below lists both so the sidebar behaves identically for the two.
const NAV_GROUPS = [
  // ---- SUPER ADMIN (platform-level) — Restyled premium sidebar with 16 categories ----
  { label: "Overview", superAdminOnly: true, items: [
    { to: "/admin/dashboard",       label: "Dashboard",             icon: LayoutDashboard, superAdminOnly: true },
    { to: "/admin/stores",          label: "Stores",                icon: StoreIcon,       superAdminOnly: true },
    { to: "/admin/master-products", label: "All Stores Products",   icon: Package,         superAdminOnly: true },
    { to: "/admin/customers",       label: "Users & Roles",         icon: Users,           superAdminOnly: true },
    { to: "/admin/delivery-zones",  label: "Delivery Management",   icon: Truck,           superAdminOnly: true },
  ]},
  { label: "Growth", superAdminOnly: true, items: [
    { to: "/admin/marketing",       label: "Marketing Center",      icon: Megaphone,        superAdminOnly: true },
    { to: "/admin/website-builder", label: "Website Builder",       icon: LayoutTemplate,   superAdminOnly: true },
    { to: "/admin/ui-customization",label: "App Management",        icon: MonitorSmartphone,superAdminOnly: true },
    { to: "/admin/visual-builder",  label: "Visual Builder",        icon: Sparkles,         superAdminOnly: true },
    { to: "/admin/deal-baazar",     label: "CMS",                   icon: FileText,         superAdminOnly: true },
    // Sprint 79 — Categories & Subcategories management is a platform-wide
    // Super Admin responsibility. Store Admins can still pick categories in
    // the product form but cannot create/edit them.
    { to: "/admin/categories",      label: "Categories",            icon: FolderTree,       superAdminOnly: true },
  ]},
  { label: "Business", superAdminOnly: true, items: [
    { to: "/admin/orders",          label: "Orders & Analytics",    icon: ShoppingBag,      superAdminOnly: true },
    { to: "#payments",              label: "Payments & Finance",    icon: Wallet,           superAdminOnly: true, comingSoon: true },
    { to: "/admin/reports",         label: "Reports",               icon: BarChart3,        superAdminOnly: true },
  ]},
  { label: "System", superAdminOnly: true, items: [
    { to: "#notifications",         label: "Notifications",         icon: Bell,             superAdminOnly: true, comingSoon: true },
    { to: "#support",               label: "Support & Tickets",     icon: HelpCircle,       superAdminOnly: true, comingSoon: true },
    { to: "/admin/profile",         label: "Settings",              icon: Settings,         superAdminOnly: true },
    { to: "#security",              label: "Security",              icon: ShieldCheck,      superAdminOnly: true, comingSoon: true },
    { to: "#logs",                  label: "System Logs",           icon: FileClock,        superAdminOnly: true, comingSoon: true },
  ]},

  // ---- STORE ADMIN (store-level) ----
  { label: "Overview", storeOnly: true, items: [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"] },
    { to: "/admin/reports",   label: "Reports",   icon: BarChart3,       rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], feature: "reports" },
  ]},
  { label: "Catalog", storeOnly: true, items: [
    { to: "/admin/products",             label: "Products",              icon: Package,    rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], feature: "products" },
    { to: "/admin/bulk-product-upload",  label: "Bulk Product Upload",   icon: UploadIcon, rolesAllowed: ["STORE_ADMIN", "STORE_OWNER"], feature: "bulk_upload" },
    { to: "/admin/brands",               label: "Brands",                icon: Tag,        rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], feature: "brands" },
    { to: "/admin/inventory",            label: "Inventory",             icon: Boxes,      rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], feature: "inventory" },
  ]},
  { label: "Operations", storeOnly: true, items: [
    { to: "/admin/pos",           label: "POS",             icon: MonitorSmartphone, rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER", "CASHIER"], feature: "pos" },
    { to: "/admin/pos/invoices",  label: "Invoice History", icon: ReceiptIcon,       rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER", "CASHIER"], feature: "pos" },
    { to: "/admin/orders",      label: "Orders",          icon: ReceiptIcon,       rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], feature: "orders" },
    { to: "/admin/suppliers",   label: "Suppliers",       icon: Truck,             rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], feature: "suppliers" },
    { to: "/admin/purchases",   label: "Purchase Orders", icon: ClipboardList,     rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], feature: "purchases" },
    { to: "/admin/warehouses",  label: "Warehouses",      icon: Warehouse,         rolesAllowed: ["STORE_ADMIN", "STORE_OWNER"], feature: "warehouses" },
  ]},
  { label: "People", storeOnly: true, items: [
    { to: "/admin/customers", label: "Customers",        icon: UserCircle2, rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], feature: "customers" },
    { to: "/admin/employees", label: "Staff Management", icon: UsersRound,  rolesAllowed: ["STORE_ADMIN", "STORE_OWNER"], feature: "employees" },
    { to: "#reviews",         label: "Reviews",          icon: Sparkles,    rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], comingSoon: true },
  ]},
  // Sprint — Phase 4 sidebar refinements.
  // Coupons & Marketing → SA only, replaced here with "Advertisement" (Coming Soon).
  // Deal Baazar stays but the admin page hides advanced controls for STORE_ADMIN.
  // Homepage Layout + Delivery Zones removed from STORE_ADMIN entirely.
  { label: "Storefront", storeOnly: true, items: [
    { to: "/admin/advertisement",   label: "Advertisement",   icon: Megaphone,       rolesAllowed: ["STORE_ADMIN", "STORE_OWNER"], comingSoon: true },
    { to: "/admin/deal-baazar",     label: "Deal Baazar",     icon: Sparkles,        rolesAllowed: ["STORE_ADMIN", "STORE_OWNER"], feature: "deal_baazar" },
    { to: "/admin/store-status",    label: "Store Status",    icon: Power,           rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], feature: "store_status" },
    { to: "/admin/store-settings",  label: "Store Settings",  icon: Settings,        rolesAllowed: ["STORE_ADMIN", "STORE_OWNER"], feature: "store_settings" },
  ]},
  // Sprint — Phase 1 Store Admin restyle. New "Finance" and "System" groups
  // surface the four remaining primary categories (Payouts, Support, Notifications)
  // as coming-soon placeholders so the high-density sidebar renders all 12 hubs.
  { label: "Finance", storeOnly: true, items: [
    { to: "#payouts",       label: "Payouts",         icon: Wallet,      rolesAllowed: ["STORE_ADMIN", "STORE_OWNER"], comingSoon: true },
  ]},
  { label: "System", storeOnly: true, items: [
    { to: "#support",       label: "Support & Tickets", icon: HelpCircle, rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], comingSoon: true },
    { to: "#notifications", label: "Notifications",     icon: Bell,       rolesAllowed: ["STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"], comingSoon: true },
  ]},
];

// Derive a page title + section from the current path. We iterate through
// role-appropriate groups first (Super Admin's "Platform Overview" vs Store
// Admin's "Overview") so the breadcrumb label always matches the sidebar the
// user actually sees. Phase 3 — URL structure is `/admin/{slug}/{page}` so we
// strip the slug segment before matching against the static NAV_GROUPS `to`
// values (which still live under `/admin/...`).
function pageMetaFor(pathname, role) {
  const isSA = role === "SUPER_ADMIN";
  // Strip `/admin/{slug}` prefix if present so we can match against static NAV_GROUPS.
  const normalised = pathname.replace(/^\/admin\/[^/]+/, "/admin");
  const groups = NAV_GROUPS.filter((g) => {
    if (g.superAdminOnly) return isSA;
    if (g.storeOnly)      return !isSA;
    return true;
  });
  for (const g of groups) {
    const hit = g.items.find((it) => normalised.startsWith(it.to));
    if (hit) return { title: hit.label, section: g.label, icon: hit.icon };
  }
  return { title: "Admin", section: isSA ? "Platform" : "Overview", icon: LayoutDashboard };
}

function AdminTopBar({ user, onOpenMobile }) {
  const canOpenPos = canPOS(user) && (user?.store_features?.pos !== false);
  const loc = useLocation();
  const { storeSlug } = useParams();
  // Slug-scoped admin URL prefix used by topbar quick links.
  const base = storeSlug ? `/admin/${storeSlug}` : "/admin";
  const meta = pageMetaFor(loc.pathname, user?.role);
  const [pending, setPending] = useState(0);
  const knownIdsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const canNotify = ["SUPER_ADMIN", "STORE_ADMIN", "STORE_OWNER", "STORE_MANAGER"].includes(user?.role);

  // Sprint 58 — New-order sound + toast + live badge.
  // Polls /orders every 20s. First poll seeds the known-id set (no toast on load).
  // Subsequent polls diff and toast + beep for genuinely new orders.
  useEffect(() => {
    if (!canNotify) return;
    let cancelled = false;
    const beep = () => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        audioCtxRef.current = audioCtxRef.current || new AC();
        const ctx = audioCtxRef.current;
        // Play two short pleasant tones — E5 then A5.
        [{ f: 659.25, s: 0 }, { f: 880, s: 0.14 }].forEach(({ f, s }) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine"; o.frequency.value = f;
          g.gain.setValueAtTime(0.0001, ctx.currentTime + s);
          g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + s + 0.03);
          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + s + 0.22);
          o.connect(g).connect(ctx.destination);
          o.start(ctx.currentTime + s);
          o.stop(ctx.currentTime + s + 0.24);
        });
      } catch { /* browsers that block audio autoplay — silently ignore */ }
    };
    const tick = async () => {
      try {
        const r = await api.get("/orders?limit=50");
        if (cancelled) return;
        const items = r.data.items || [];
        const active = items.filter((o) =>
          ["confirmed", "packed", "ready", "assigned", "out_for_delivery", "placed"].includes(o.status)
        );
        setPending(active.length);
        const ids = new Set(items.map((o) => o.id));
        if (knownIdsRef.current === null) {
          knownIdsRef.current = ids;             // seed on first poll
          return;
        }
        const fresh = items.filter((o) => !knownIdsRef.current.has(o.id));
        knownIdsRef.current = ids;
        if (fresh.length > 0) {
          beep();
          const first = fresh[0];
          // Sprint 59 — richer toast: order id, customer name, phone, address,
          // and (multi-store admins only) the store name. One-tap "Open" jumps
          // straight to the order.
          const addr = first.address || {};
          const cust = first.customer_name || first.user_name || addr.name || "Customer";
          const phone = first.customer_phone || first.user_phone || addr.phone || "";
          const line = [addr.line1, addr.city, addr.pincode].filter(Boolean).join(", ");
          const isMulti = user?.role === "SUPER_ADMIN";
          const store = first.store_name || (first.store && first.store.name);
          const descLines = [
            cust + (phone ? ` · ${phone}` : ""),
            line || null,
            isMulti && store ? `Store: ${store}` : null,
            fresh.length > 1 ? `+${fresh.length - 1} more new order${fresh.length - 1 === 1 ? "" : "s"}` : null,
          ].filter(Boolean);
          toast.success(
            `🛒 New order ${first.order_number || ""}`,
            {
              description: descLines.join(" · "),
              action: { label: "Open", onClick: () => window.location.assign(`/admin/orders/${first.id}`) },
              duration: 10000,
            }
          );
        }
      } catch { /* silent — badge stays as-is on transient failure */ }
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [canNotify]);

  const Icon = meta.icon;
  return (
    <header
      className="sticky top-0 z-30 h-14 md:h-16 bg-card/85 backdrop-blur border-b flex items-center gap-3 px-3 md:px-6"
      data-testid="admin-topbar"
    >
      <Button variant="ghost" size="icon" className="md:hidden" onClick={onOpenMobile} data-testid="admin-menu-btn">
        <Menu className="w-5 h-5" />
      </Button>

      {/* Breadcrumb + page title */}
      <div className="flex items-center gap-2 md:gap-3 min-w-0">
        <div className="hidden md:flex w-9 h-9 rounded-lg bg-primary/10 text-primary items-center justify-center shrink-0">
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div className="min-w-0">
          <div className="hidden md:block text-[10.5px] uppercase tracking-wider text-muted-foreground/80 font-semibold leading-none">
            {meta.section}
          </div>
          <div className="font-display text-[15px] md:text-lg font-semibold leading-tight truncate">
            {meta.title}
          </div>
        </div>
      </div>

      {/* Center global search (desktop) */}
      <div className="ml-auto hidden lg:flex flex-1 max-w-md">
        <Link
          to="/admin/products"
          className="w-full h-10 pl-10 pr-3 rounded-lg border border-border/80 bg-secondary/50 hover:bg-secondary text-sm text-muted-foreground flex items-center relative transition-colors"
          data-testid="topbar-search"
        >
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" />
          <span>Search products, orders, customers…</span>
          <kbd className="ml-auto text-[10px] font-mono bg-card border rounded px-1.5 py-0.5 shrink-0">/</kbd>
        </Link>
      </div>

      <div className="ml-auto lg:ml-0 flex items-center gap-1 md:gap-2">
        {/* Sprint 59 — POS button gets extra visual prominence: pulse ring, bigger padding,
            sits directly next to the notification bell for one-tap access. */}
        {canOpenPos && (
          <Link
            to={`${base}/pos`}
            className="relative inline-flex items-center gap-1.5 h-9 px-3 md:px-3.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wide shadow-sm hover:brightness-105 transition ring-2 ring-primary/25"
            data-testid="topbar-pos-btn"
            title="Open Point of Sale"
          >
            <MonitorSmartphone className="w-4 h-4" />
            <span className="hidden sm:inline">POS</span>
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-card animate-pulse" aria-hidden="true" />
          </Link>
        )}
        <Link
          to={`${base}/orders`}
          className="relative w-9 h-9 rounded-lg grid place-items-center hover:bg-secondary transition"
          title={`${pending} pending order${pending === 1 ? "" : "s"}`}
          data-testid="topbar-notif"
        >
          <Bell className="w-4.5 h-4.5" />
          {pending > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold grid place-items-center">
              {pending > 99 ? "99+" : pending}
            </span>
          )}
        </Link>
        <NavLink to={`${base}/profile`} className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-lg hover:bg-secondary" data-testid="topbar-profile">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-accent text-white grid place-items-center text-[11px] font-bold">
            {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="hidden md:block leading-tight">
            <div className="text-[12px] font-semibold truncate max-w-[110px]">{user?.name || user?.email}</div>
            <div className="text-[9.5px] uppercase tracking-wide text-muted-foreground">{user?.role}</div>
          </div>
        </NavLink>
      </div>
    </header>
  );
}

export default function AppShell() {
  const { user, logout, isImpersonating, endImpersonation } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const { storeSlug: urlSlug } = useParams();
  const pos = canPOS(user);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  // Phase 3 — Store-slug scoped routing.
  //
  // Every admin URL now looks like `/admin/{store-slug}/{page}`. The shell:
  //   1. Enforces access: non-SA users may only visit their own slug. If the
  //      URL slug doesn't match, we redirect them to their own dashboard.
  //   2. For SUPER_ADMIN, we resolve `slug → store_id` once and stash the id
  //      in localStorage so every subsequent API call is scoped via the
  //      existing `X-Store-Id` header. SA visiting `/admin/platform/*` (the
  //      synthetic slug from `AdminLandingRedirect`) means "no store scope".
  const isSA = user?.role === "SUPER_ADMIN";
  const [slugStoreId, setSlugStoreId] = useState(null);
  const [slugResolved, setSlugResolved] = useState(false);

  useEffect(() => {
    if (!user || !urlSlug) { setSlugResolved(true); return; }
    let alive = true;
    // Phase 3 — Legacy URL upgrade. Existing hardcoded `<Link to="/admin/foo">`
    // in various pages will land here with `urlSlug = "foo"`. We detect that
    // by comparing against the set of known admin pages, and transparently
    // rewrite the URL to `/admin/{userSlug}/{legacy}` so every deep link keeps
    // working without touching each file.
    const KNOWN_PAGES = new Set([
      "dashboard", "products", "bulk-product-upload", "pos", "categories",
      "brands", "inventory", "suppliers", "purchases", "warehouses",
      "employees", "stores", "ui-customization", "customers", "orders",
      "marketing", "advertisement", "deal-baazar", "deal-bazaar", "homepage-layout",
      "website-builder",
      "store-status", "delivery-zones", "store-settings", "profile", "reports",
    ]);
    if (KNOWN_PAGES.has(urlSlug)) {
      const rest = loc.pathname.replace(/^\/admin/, "");
      const userSlug = user.store_slug || "platform";
      navigate(`/admin/${userSlug}${rest}`, { replace: true });
      return;
    }
    (async () => {
      if (isSA) {
        if (urlSlug === "platform") {
          // SA on the synthetic `platform` slug — no store scope.
          try { authStore.setStoreId("", "SUPER_ADMIN"); localStorage.removeItem("super_admin_store_id"); } catch { /* noop */ }
          if (alive) { setSlugStoreId(null); setSlugResolved(true); }
          return;
        }
        try {
          const { data } = await api.get("/stores?limit=100");
          const match = (data.items || []).find((s) => s.slug === urlSlug);
          if (match?.id) {
            try { authStore.setStoreId(match.id, "SUPER_ADMIN"); } catch { /* noop */ }
            if (alive) { setSlugStoreId(match.id); setSlugResolved(true); }
          } else if (alive) {
            toast.error("Store not found");
            navigate("/admin", { replace: true });
          }
        } catch {
          if (alive) setSlugResolved(true);
        }
      } else {
        // Non-SA: if the URL slug doesn't match their store, redirect. We can
        // trust `user.store_slug` because it's set server-side on /auth/me.
        if (user.store_slug && urlSlug !== user.store_slug) {
          navigate(`/admin/${user.store_slug}/dashboard`, { replace: true });
          return;
        }
        // No slug on the user profile yet (impersonation or older account) →
        // fall through and let the request 403 naturally.
        if (alive) setSlugResolved(true);
      }
    })();
    return () => { alive = false; };
  }, [urlSlug, user, isSA, navigate, loc.pathname]);

  // Build the URL prefix once, then rewrite every NAV_GROUPS `to` on render.
  const base = urlSlug ? `/admin/${urlSlug}` : "/admin";

  const exitImpersonation = () => {
    if (endImpersonation()) {
      window.location.assign("/admin");
    }
  };

  // Dedicated full-screen workstation view for POS terminal
  const isPosPage = loc.pathname.endsWith("/pos") || loc.pathname.endsWith("/pos/");
  if (isPosPage) {
    return (
      <div className="h-screen w-screen overflow-hidden bg-[#f8fafc] flex flex-col">
        {isImpersonating && (
          <div
            className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-black text-[12.5px] font-semibold flex items-center justify-center gap-3 py-1.5 px-3 shadow-md"
            data-testid="impersonation-banner"
          >
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-black/70 animate-pulse" aria-hidden="true" />
              <span className="truncate">
                Viewing as {user?.name || user?.email} · {user?.role}
              </span>
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={exitImpersonation}
              className="h-7 px-2 bg-black/10 hover:bg-black/20 text-black"
              data-testid="exit-impersonation-btn"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Back to Super Admin
            </Button>
          </div>
        )}
        <main className={`flex-1 min-w-0 h-full overflow-hidden ${isImpersonating ? "pt-9" : ""}`}>
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen md:flex bg-background ${isSA ? "sa-shell" : ""}`}>
      {/* Sprint — Impersonation banner. Shown as a slim yellow strip at the
          very top when a Super Admin has switched into a store owner. */}
      {isImpersonating && (
        <div
          className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-black text-[12.5px] font-semibold flex items-center justify-center gap-3 py-2 px-3 shadow-md"
          data-testid="impersonation-banner"
        >
          <span className="inline-flex items-center gap-2 min-w-0">
            <span className="w-2 h-2 rounded-full bg-black/70 animate-pulse" aria-hidden="true" />
            <span className="truncate">
              Viewing as {user?.name || user?.email} · {user?.role}
            </span>
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={exitImpersonation}
            className="h-7 px-2 bg-black/10 hover:bg-black/20 text-black"
            data-testid="exit-impersonation-btn"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Back to Super Admin
          </Button>
        </div>
      )}
      {/* Mobile backdrop */}
      {mobileOpen && <div className="md:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm" onClick={closeMobile} />}

      {/* Sidebar — sticky on desktop, slide-in drawer on mobile */}
      <aside
        className={`w-64 shrink-0 border-r bg-card flex-col md:sticky md:top-0 md:h-screen md:flex fixed inset-y-0 left-0 z-50 flex transition-transform duration-200 shadow-xl md:shadow-none ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        data-testid="admin-sidebar"
      >
        <div className="px-4 md:px-5 py-4 md:py-5 border-b flex items-center gap-3 h-14 md:h-16">
          <BrandLogo size={32} className="shrink-0" />
          <Button variant="ghost" size="icon" className="md:hidden ml-auto" onClick={closeMobile} data-testid="admin-sidebar-close"><X className="w-5 h-5" /></Button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2.5 space-y-5" onClick={closeMobile}>
          {NAV_GROUPS
            .filter((g) => {
              if (g.superAdminOnly) return user?.role === "SUPER_ADMIN";
              if (g.storeOnly)      return user?.role !== "SUPER_ADMIN";
              return true;
            })
            .map((group) => {
              const feats = user?.store_features || null; // null = SA or not-loaded → allow all
              const visibleItems = group.items
                .filter((it) => !it.superAdminOnly || user?.role === "SUPER_ADMIN")
                .filter((it) => !it.rolesAllowed || it.rolesAllowed.includes(user?.role))
                // Sprint — Per-store feature toggle gate. Skip for SUPER_ADMIN
                // (always-on) and items without an explicit `feature` key.
                .filter((it) => {
                  if (user?.role === "SUPER_ADMIN") return true;
                  if (!it.feature) return true;
                  if (!feats) return true;                 // unknown map → allow
                  return feats[it.feature] !== false;      // explicit false → hide
                });
              if (visibleItems.length === 0) return null;    // Sprint 58 — hide empty groups
              return (
                <div key={group.label}>
                  <div className="px-3 pb-1.5 text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
                    {group.label}
                  </div>
                  <div className="space-y-0.5">
                    {visibleItems.map(({ to, label, icon: Icon, comingSoon }) => {
                      // Phase 3 — All admin URLs are `/admin/{slug}/*`. Strip
                      // the hardcoded `/admin` prefix from NAV_GROUPS and
                      // splice in the current slug so every link stays scoped.
                      // Coming-soon items use `#foo` — render as inert button.
                      const isPlaceholder = comingSoon && to.startsWith("#");
                      if (isPlaceholder) {
                        return (
                          <button
                            key={to}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toast.info(`${label} — coming soon`); }}
                            className="w-full group relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-foreground/60 hover:bg-secondary/60 hover:text-foreground/80 transition-all"
                            data-testid={`nav-soon-${to.replace("#", "")}`}
                          >
                            <Icon className="w-[17px] h-[17px] shrink-0" />
                            <span className="flex-1 truncate text-left">{label}</span>
                            <span className="text-[8.5px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5">
                              Soon
                            </span>
                          </button>
                        );
                      }
                      const relPath = to.replace(/^\/admin/, "");
                      const target  = `${base}${relPath}`;
                      return (
                      <NavLink
                        key={target}
                        to={target}
                        className={({ isActive }) =>
                          `group relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-all ${
                            isActive
                              ? "bg-primary/12 text-primary font-semibold shadow-sm"
                              : "text-foreground/75 hover:bg-secondary hover:text-foreground"
                          }`
                        }
                        data-testid={`nav-${to.replace("/admin/", "")}`}
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-primary" aria-hidden="true" />}
                            <Icon className={`w-[17px] h-[17px] shrink-0 ${isActive ? "text-primary" : ""}`} />
                            <span className="flex-1 truncate">{label}</span>
                            {comingSoon && (
                              <span className="text-[8.5px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5" data-testid={`nav-badge-${to.replace("/admin/", "")}`}>
                                Soon
                              </span>
                            )}
                            {!comingSoon && (
                              <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-50 transition-opacity" />
                            )}
                          </>
                        )}
                      </NavLink>
                    );})}
                  </div>
                </div>
              );
            })}
        </nav>

        <div className="p-3 border-t bg-secondary/30">
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="w-full justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/5"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main column with sticky top bar */}
      <div className={`flex-1 min-w-0 flex flex-col ${isImpersonating ? "pt-9" : ""} pb-16 md:pb-0`} key={loc.pathname}>
        <AdminTopBar user={user} onOpenMobile={() => setMobileOpen(true)} />
        <main className="flex-1 min-w-0 overflow-x-hidden">
          <Outlet />
        </main>
        {/* Sprint 80 — Hide the admin mobile bottom nav on the POS page so its
            own sticky cart / CONTINUE bar has full-width space. */}
        {!/\/pos(\/|$)/.test(loc.pathname) && (
          <MobileBottomNav base={base} pathname={loc.pathname} isSA={isSA} onOpenMore={() => setMobileOpen(true)} />
        )}
      </div>
    </div>
  );
}

// Sprint — Premium mobile bottom nav. 5 primary hubs, sticky at the viewport
// bottom on <md screens only. Reuses the same slug-scoped URL prefix as the
// sidebar so every link stays inside the current store scope.
// Super Admin gets platform-level tabs; Store Admin gets store-scoped tabs
// with a "More" button that opens the full sidebar drawer.
//
// Sprint 73 — Auto-hide on scroll (Blinkit-style): slides down on scroll-down
// past ~60px, slides back up on scroll-up. Maximises screen space during
// POS billing and long product-list browsing on mobile.
function MobileBottomNav({ base, pathname, isSA, onOpenMore }) {
  const saItems = [
    { to: `${base}/dashboard`,      label: "Home",      icon: LayoutDashboard, testId: "mb-dashboard" },
    { to: `${base}/stores`,         label: "Stores",    icon: StoreIcon,       testId: "mb-stores" },
    { to: `${base}/orders`,         label: "Orders",    icon: ShoppingBag,     testId: "mb-orders" },
    { to: `${base}/marketing`,      label: "Marketing", icon: Megaphone,       testId: "mb-marketing" },
    { to: `${base}/reports`,        label: "Reports",   icon: BarChart3,       testId: "mb-reports" },
  ];
  const storeItems = [
    { to: `${base}/dashboard`, label: "Dashboard", icon: LayoutDashboard, testId: "mb-store-dashboard" },
    { to: `${base}/products`,  label: "Products",  icon: Package,         testId: "mb-store-products" },
    { to: `${base}/orders`,    label: "Orders",    icon: ShoppingBag,     testId: "mb-store-orders" },
    { to: `${base}/reports`,   label: "Reports",   icon: BarChart3,       testId: "mb-store-reports" },
  ];
  const items = isSA ? saItems : storeItems;

  // Auto-hide on scroll — matches customer MobileBottomNav behaviour.
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      const delta = y - lastY.current;
      if (y < 60) setHidden(false);
      else if (delta > 6) setHidden(true);
      else if (delta < -6) setHidden(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Reset on route change
  useEffect(() => { setHidden(false); lastY.current = 0; }, [pathname]);

  return (
    <nav
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 bg-card/95 backdrop-blur border-t border-border/70 pb-[env(safe-area-inset-bottom)] transition-transform duration-300 ease-out ${hidden ? "translate-y-full" : "translate-y-0"}`}
      data-testid="mobile-bottom-nav"
      data-hidden={hidden ? "true" : "false"}
    >
      <div className={`grid ${isSA ? "grid-cols-5" : "grid-cols-5"} h-16`}>
        {items.map(({ to, label, icon: Icon, testId }) => {
          const active = pathname.startsWith(to);
          return (
            <Link
              key={to}
              to={to}
              className={`flex flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold transition-colors ${
                active ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={testId}
            >
              <div className={`w-10 h-6 rounded-full grid place-items-center transition-all ${active ? "bg-primary/12" : ""}`}>
                <Icon className={`w-[18px] h-[18px] ${active ? "scale-110" : ""} transition-transform`} />
              </div>
              <span className="truncate max-w-full px-1">{label}</span>
            </Link>
          );
        })}
        {!isSA && (
          <button
            type="button"
            onClick={onOpenMore}
            className="flex flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
            data-testid="mb-store-more"
          >
            <div className="w-10 h-6 rounded-full grid place-items-center">
              <Menu className="w-[18px] h-[18px]" />
            </div>
            <span className="truncate max-w-full px-1">More</span>
          </button>
        )}
      </div>
    </nav>
  );
}
